import { useState } from 'react';
import { Search, KeyRound, Menu, Sun, Moon } from 'lucide-react';
import { useApiKey } from '@/stores/apiKey.store';
import { useThemeStore } from '@/stores/theme.store';
import ResourcesMenu from '../resources/ResourcesMenu';
import McpConfigModal from '../mcp/McpConfigModal';

interface Props {
    onSearchClick: () => void;
    onMenuClick?: () => void;
}

export default function TopBar({ onSearchClick, onMenuClick }: Props) {
    const { key } = useApiKey();
    const masked = key ? `${key.slice(0, 8)}…${key.slice(-4)}` : null;
    const [mcpOpen, setMcpOpen] = useState(false);
    const theme = useThemeStore((s) => s.theme);
    const toggleTheme = useThemeStore((s) => s.toggle);

    return (
        <header
            className="h-14 flex items-center justify-between gap-2 px-4 sm:px-6 sticky top-0 z-30"
            style={{
                borderBottom: '1px solid var(--border)',
                backgroundColor: 'var(--bg)',
            }}
        >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                {/* Hamburger (mobile only) */}
                <button
                    onClick={onMenuClick}
                    className="md:hidden p-2 -ml-1 rounded-md text-muted hover:text-ink hover:bg-surface-1 transition-colors"
                    aria-label="Open navigation menu"
                >
                    <Menu className="h-5 w-5" />
                </button>

                <a
                    href="/"
                    className="flex items-center gap-2 min-w-0"
                    aria-label="SatuTim API Playground — kembali ke beranda"
                >
                    <img
                        src="/satutim-logo-nobg.png"
                        alt=""
                        className="h-6 w-6 shrink-0 object-contain"
                        width="24"
                        height="24"
                        decoding="async"
                    />
                    <span className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>SatuTim</span>
                    <span className="text-sm hidden sm:inline" style={{ color: 'var(--muted)' }}>
                        / API Playground
                    </span>
                </a>
            </div>

            {/* Desktop search bar — GitBook style: subtle, minimal */}
            <button
                onClick={onSearchClick}
                className="hidden md:flex items-center gap-2 px-3 h-8 rounded-md text-sm transition-colors"
                style={{
                    backgroundColor: 'var(--surface-1)',
                    border: '1px solid var(--border)',
                    color: 'var(--muted)',
                    minWidth: 320,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-strong)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                }}
            >
                <Search className="h-3.5 w-3.5" />
                <span>Cari endpoint…</span>
                <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded" style={{
                    backgroundColor: 'var(--bg)',
                    border: '1px solid var(--border)',
                    color: 'var(--ghost)',
                }}>⌘K</kbd>
            </button>

            <div className="flex items-center gap-1 shrink-0">
                {/* Mobile-only search icon */}
                <button
                    onClick={onSearchClick}
                    className="md:hidden p-2 rounded-md text-muted hover:text-ink hover:bg-surface-1 transition-colors"
                    aria-label="Cari endpoint"
                >
                    <Search className="h-4 w-4" />
                </button>

                {/* Theme toggle */}
                <button
                    onClick={toggleTheme}
                    className="p-2 rounded-md text-muted hover:text-ink hover:bg-surface-1 transition-colors"
                    aria-label={theme === 'dark' ? 'Pakai mode terang' : 'Pakai mode gelap'}
                    title={theme === 'dark' ? 'Pakai mode terang' : 'Pakai mode gelap'}
                >
                    {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>

                <ResourcesMenu onMcpClick={() => setMcpOpen(true)} />
                {masked ? (
                    <span className="hidden sm:flex items-center gap-1.5 text-xs px-2 py-1 rounded-md font-mono" style={{
                        backgroundColor: 'var(--accent-bg)',
                        color: 'var(--accent)',
                        border: '1px solid var(--accent-border)',
                    }}>
                        <KeyRound className="h-3 w-3" />
                        {masked}
                    </span>
                ) : (
                    <span className="hidden sm:flex items-center gap-1.5 text-xs px-2 py-1 rounded-md" style={{
                        backgroundColor: 'var(--surface-1)',
                        color: 'var(--muted)',
                        border: '1px solid var(--border)',
                    }}>
                        <KeyRound className="h-3 w-3" />
                        Belum aktif
                    </span>
                )}
            </div>

            <McpConfigModal open={mcpOpen} onClose={() => setMcpOpen(false)} />
        </header>
    );
}
